
import Shifty from '@deepsource/shifty'
import { defineComponent, PropType } from 'vue'
import { ReportMeta, ReportPageT } from '~/types/reportTypes'
import {
  CreatePublicReportInput,
  ReportLevel,
  ReportType,
  UpdatePublicReportInput
} from '~/types/types'

/**
 * ? Why create and update in same modal
 *
 * On an individual report page, we have a share button. On clicking it, this modal opens with
 * the reportKeys prefilled, which is done through reprtoKeys being prop controlled.
 * Since we'll anyways need to support prop controlled reportKeys, we added support for
 * all data properties to be prop controlled. Hence this modal works for both, report creation and updation.
 */

/**
 * Create Public Report modal
 */
export default defineComponent({
  name: 'MutateReportModal',
  props: {
    level: {
      type: String as PropType<ReportLevel>,
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    },
    reportIdOld: {
      type: String,
      default: ''
    },
    isRestrictedOld: {
      type: Boolean,
      default: false
    },
    reportKeysOld: {
      type: Array as PropType<Array<ReportPageT>>,
      default: () => []
    },
    shareHistoricalDataOld: {
      type: Boolean,
      default: false
    },
    reportLabelOld: {
      type: String,
      default: ''
    },
    saveLoading: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    '$route.path'() {
      this.closeModal()
    }
  },
  data() {
    return {
      isRestricted: false,
      isPasswordHidden: false,
      password: '',
      reportKeys: [] as Array<ReportPageT>,
      shareHistoricalData: false,
      reportLabel: '',
      showPasswordInput: false,
      engine: null as Shifty | null,
      ReportMeta,
      ReportType
    }
  },
  mounted() {
    this.reportKeys = this.reportKeysOld
    this.shareHistoricalData = this.shareHistoricalDataOld
    this.reportLabel = this.reportLabelOld
    this.isRestricted = this.isRestrictedOld
  },
  computed: {
    reportUrl(): string {
      return `https://${this.$config.domain}/report/${this.reportIdOld}`
    }
  },
  methods: {
    /**
     * Method to handle password-protected toggle updation and, based on value received from it,
     * control visibility of password input field .
     *
     * @param passworProtected boolean
     *
     * @return void
     */
    handlePasswordProtectedToggle(passworProtected: boolean) {
      /**
       * We receive new value of password-protected toggle as argument.
       * If this new value is true, that means we need to show the password input field,
       * for which we need to also generate a new password.
       *
       * However, we want to show the password input field only if the report was not
       * password-protected already, because in that case we wan't to show the password
       * reset button.
       */
      if (passworProtected) {
        if (!this.isRestrictedOld) {
          this.generatePassword()
          this.showPasswordInput = true
        }
      } else {
        this.password = ''
        this.showPasswordInput = false
      }
    },

    /**
     * Click handler method for password reset button.
     * Generates new password and makes password input field visible.
     *
     * @return void
     */
    handleResetPassword() {
      this.generatePassword()
      this.showPasswordInput = true
    },

    /**
     * Click handler button for save report button.
     *
     * @oaram close {() => void}
     *
     * @return void
     */
    handleSave(close?: () => void) {
      if (this.editMode) {
        this.emitEditReport(close)
      } else {
        this.emitCreateReport(close)
      }
    },

    /**
     * Method to build args for report creation and emit it.
     *
     * @oaram close {() => void}
     *
     * @return void
     */
    emitCreateReport(close?: () => void) {
      if (this.validateArgs()) {
        const { provider, owner: ownerLogin, repo: repositoryName } = this.$route.params
        const newReportArgs: CreatePublicReportInput = {
          ownerLogin,
          repositoryName,
          vcsProvider: this.$providerMetaMap[provider].value,
          label: this.reportLabel,
          level: this.level,
          isRestricted: this.isRestricted,
          password: this.password,
          shareHistoricalData: this.shareHistoricalData,
          reportKeys: this.reportKeys
        }

        if (Object.keys(newReportArgs).length) {
          this.$emit('create-report', newReportArgs, close)
        }
      }
    },

    /**
     * Method to build args for report updation and emit it.
     *
     * @oaram close {() => void}
     *
     * @return void
     */
    emitEditReport(close?: () => void) {
      if (this.validateArgs()) {
        const updatedReportArgs: UpdatePublicReportInput = {
          reportId: this.reportIdOld,
          label: this.reportLabel,
          shareHistoricalData: this.shareHistoricalData,
          reportKeys: this.reportKeys,
          isRestricted: this.isRestricted
        }

        if (this.isRestricted && this.password.length) {
          updatedReportArgs['password'] = this.password
        }

        if (Object.keys(updatedReportArgs).length) {
          this.$emit('edit-report', updatedReportArgs, close)
        }
      }
    },

    /**
     * Generate password using shifty
     *
     * @return {void}
     */
    generatePassword(): void {
      if (!this.engine) this.engine = new Shifty(false, 16)

      this.password = this.engine.generate()
      if (this.isPasswordHidden) {
        this.isPasswordHidden = false
        setTimeout(() => {
          this.isPasswordHidden = true
        }, 1500)
      }
    },

    /**
     * Toggle password visibility
     *
     * @return {void}
     */
    togglePasswordVisibility(): void {
      this.isPasswordHidden = !this.isPasswordHidden
    },

    /**
     * Validate arguments for create report mutation
     *
     * @return boolean
     */
    validateArgs(): boolean {
      // Check for password only if password input is shown
      if (this.showPasswordInput && !this.password) {
        this.$toast.danger("Please enter a password or disable 'Restrict report access'.")
        return false
      }

      if (this.showPasswordInput && this.password && this.password.length < 8) {
        this.$toast.danger('Password must be at least 8 characters long.')
        return false
      }

      if (!this.reportLabel) {
        this.$toast.danger('Please add a label for the report.')
        return false
      }

      if (this.reportKeys.length < 1) {
        this.$toast.danger('Please select at least one report to share.')
        return false
      }

      return true
    },

    /**
     * Close modal on route change
     *
     * @return {void}
     */
    closeModal(): void {
      this.$emit('close')
    }
  }
})
